import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import {
  Grid,
  TextField,
  InputAdornment,
  Typography,
  Paper,
} from '@mui/material';
import { useApiGet } from '../../utils/httpClientTs';
import { getTLCList } from '../../endpoints/tlc';
import { TLCListParams, TLCListItem } from '../../types/tlc';
import ServerSideTable, {
  ServerSideTableColumn,
} from '../../Components/ServerSideTable';
import SPDSelector from '../../Components/SPDSelector';
import FeatureDescription from '../../Components/FeatureDescription';
import { TLC_DESCRIPTION } from '../../constants/featureDescriptions';
import { SortOrder } from '../../types/table';

const TLCList: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const updateSearchParams = useCallback(
    (key: string, value: string) => {
      if (!value || value === '') {
        searchParams.delete(key);
      } else {
        searchParams.set(key, value);
      }
      setSearchParams(searchParams);
    },
    [searchParams, setSearchParams]
  );

  const [order, setOrder] = useState<SortOrder>('asc');
  const [orderBy, setOrderBy] = useState<keyof TLCListItem>('name');
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(25);
  const [search, setSearch] = useState<string>(
    searchParams.get('search') || ''
  );
  const [delaySearch, setDelaySearch] = useState<string>(
    searchParams.get('search') || ''
  );
  const [spdSelected, setSpdSelected] = useState<string>(
    searchParams.get('spd') || ''
  );

  // need to update search params when search changes
  useEffect(() => {
    updateSearchParams('search', search);
  }, [search, updateSearchParams]);

  // need to update search params when spdSelected changes
  useEffect(() => {
    updateSearchParams('spd', spdSelected);
  }, [spdSelected, updateSearchParams]);

  const params: TLCListParams = {
    page: page + 1, // API uses 1-based indexing
    page_size: rowsPerPage,
    search: search || undefined,
    order_by: orderBy || undefined,
    order: order || undefined,
    spd: spdSelected || undefined,
  };

  const { data, isLoading } = useApiGet(
    ['tlc-list', rowsPerPage, page, orderBy, order, search, spdSelected],
    () => getTLCList(params),
    {
      enabled: true,
      retry: 1,
    }
  );

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setSearch(delaySearch);
      setPage(0); // Reset to first page when search changes
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [delaySearch]);

  const handleClick = (tlc: TLCListItem) => {
    navigate(`/tlc/${tlc.id}`);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  const handleSort = (property: keyof TLCListItem) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
    setPage(0);
  };

  const columns: ServerSideTableColumn<TLCListItem>[] = [
    {
      id: 'name',
      label: 'Name',
      width: '50%',
      firstCell: true,
    },
    {
      id: 'state',
      label: 'State',
      width: '30%',
    },
    {
      id: 'is_active',
      label: 'Active?',
      width: '20%',
      render: (value: string) => value,
    },
  ];

  return (
    <Grid container paddingLeft={2} paddingRight={2}>
      <Grid item xs={12} padding={1} textAlign={'center'}>
        <Typography variant="h4" color={'primary'}>
          TLCs
        </Typography>
      </Grid>
      <Grid item xs={12} paddingBottom={4}>
        <FeatureDescription
          shortText={TLC_DESCRIPTION.short}
          longText={TLC_DESCRIPTION.long}
        />
      </Grid>
      <Grid container>
        <Grid item xs={6} paddingRight={2} paddingBottom={3}>
          <TextField
            label="Search"
            id="search-field"
            size="small"
            value={delaySearch}
            fullWidth={true}
            onChange={(event) => setDelaySearch(event.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3} paddingBottom={3}>
          <SPDSelector
            spd={spdSelected}
            handleSpdSelect={(setSpd) => {
              setSpdSelected(setSpd);
            }}
            keyId="tlc"
            all={true}
          />
        </Grid>
      </Grid>
      <Paper sx={{ p: 2, width: '100%' }}>
        <ServerSideTable
          data={data?.data || []}
          columns={columns}
          loading={isLoading}
          page={page}
          rowsPerPage={rowsPerPage}
          totalCount={data?.pagination?.total_count || 0}
          totalPages={data?.pagination?.total_pages || 0}
          hasNext={data?.pagination?.has_next || false}
          hasPrev={data?.pagination?.has_prev || false}
          order={order}
          orderBy={String(orderBy)}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          onSort={handleSort}
          onRowClick={handleClick}
          emptyMessage="No TLCs found."
        />
      </Paper>
    </Grid>
  );
};

export default TLCList;
